import {addQueryToPath} from '../../libs/shared/helpers/src/browser/urlHelpers';
import {AppRoutesPrefixesEnum} from '../components/appRoutesPrefixes';
import {CardModel} from '../models/api/cardModel';
import {CrmSyncSourcesEnum} from '../models/api/crmSyncModel';
import {
  ContactsManagerParametersTypesEnum,
  ContactsManagerSearchTabsEnum,
} from '../store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {computeIsGlobalNamespace, globalNamespace, isTeammateNamespace} from './models/namespaceHelpers';

/*
 * Constants.
 */

export const contactsManagerAccountsUrl = `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}`;
export const contactsLearnMoreUrl =
  'https://help.front.com/t/35h8j2t/understanding-front-contacts#contact-manager';
export const contactsManagerCardGroupsLearnMoreUrl =
  'https://help.front.com/t/y724vy/how-to-create-and-message-a-contact-group';

/*
 * Interfaces.
 */

interface ContactsManagerRouteOptions {
  cardId?: number;
  cardGroupId?: number;
  accountId?: number;
  namespace?: string;
}

/*
 * Route Helpers.
 */

export function buildContactsManagerPrivateContactsUrl(namespace: string, cardId?: number) {
  const route = `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/${namespace}`;
  return maybeAddCardIdToRoute(route, cardId);
}

export function buildContactsManagerCompanyContactsUrl(cardId?: number) {
  const route = `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/${globalNamespace}`;
  return maybeAddCardIdToRoute(route, cardId);
}

export function buildContactsManagerOtherContactsUrl(cardId?: number) {
  const route = `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/other`;
  return maybeAddCardIdToRoute(route, cardId);
}

export function buildContactsManagerCardGroupContactsUrl(cardGroupId: number, cardId?: number) {
  const route = `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/lists/${cardGroupId}`;
  return maybeAddCardIdToRoute(route, cardId);
}

export function makeContactsManagerCardGroupsUrl() {
  const route = `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/lists/all`;
  return route;
}

export function buildContactsManagerSearchUrl(query: string, tab = ContactsManagerSearchTabsEnum.CONTACTS) {
  return addQueryToPath(`/${AppRoutesPrefixesEnum.CONTACTS_MANAGER}/search/${encodeURIComponent(query)}`, {
    tab,
  });
}

export function buildContactsManagerAccountSyncsUrl(source: CrmSyncSourcesEnum, shouldShowErrors?: boolean) {
  if (source === CrmSyncSourcesEnum.MICROSOFT_DYNAMICS) {
    return buildContactsManagerSyncsUrl(source, shouldShowErrors);
  }
  const route = `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}/syncs/${source}`;
  if (shouldShowErrors) {
    return `${route}/errors`;
  }
  return route;
}

// Full page syncs route
export function buildContactsManagerSyncsUrl(source: CrmSyncSourcesEnum, shouldShowErrors?: boolean) {
  const route = `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER}/syncs/${source}`;
  if (shouldShowErrors) {
    return `${route}/errors`;
  }
  return route;
}

export function buildContactsManagerAccountCsvImportUrl() {
  return `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}/csv-import`;
}

function maybeAddCardIdToRoute(route: string, cardId?: number) {
  if (!cardId) {
    return route;
  }
  return `${route}/${cardId}`;
}

export function buildContactsManagerContactsUrl(
  contactsManagerRouteType: ContactsManagerParametersTypesEnum,
  options: ContactsManagerRouteOptions,
) {
  switch (contactsManagerRouteType) {
    case ContactsManagerParametersTypesEnum.COMPANY_CONTACTS:
      return buildContactsManagerCompanyContactsUrl(options.cardId);
    case ContactsManagerParametersTypesEnum.PRIVATE_CONTACTS:
      if (!options?.namespace) {
        return undefined;
      }
      return buildContactsManagerPrivateContactsUrl(options.namespace, options.cardId);
    case ContactsManagerParametersTypesEnum.OTHER_CONTACTS:
      return buildContactsManagerOtherContactsUrl(options.cardId);
    case ContactsManagerParametersTypesEnum.LIST_CONTACTS:
      if (!options?.cardGroupId) {
        return undefined;
      }
      return buildContactsManagerCardGroupContactsUrl(options.cardGroupId, options.cardId);
    case ContactsManagerParametersTypesEnum.ACCOUNT_CONTACTS:
      if (!options?.accountId) {
        return undefined;
      }
      return buildContactsManagerAccountContactsUrl(options.accountId, options.cardId);
    default:
      return undefined;
  }
}

export function buildContactsManagerAccountsUrl() {
  return `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}`;
}

export function makeContactsManagerAccountDetailsUrl(accountId: number) {
  return `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}/${accountId}`;
}

export function buildContactsManagerAccountContactsUrl(accountId: number, cardId?: number) {
  const route = `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}/${accountId}/contacts`;
  return maybeAddCardIdToRoute(route, cardId);
}

export function buildUrlToNavigateToCardInContactsManager(card: CardModel) {
  if (computeIsGlobalNamespace(card.namespace)) {
    return buildContactsManagerCompanyContactsUrl(card.id);
  }
  if (isTeammateNamespace(card.namespace)) {
    return buildContactsManagerPrivateContactsUrl(card.namespace, card.id);
  }
  return buildContactsManagerOtherContactsUrl(card.id);
}
